import { z } from "zod";
export const DeviceSchema = z.object({
    publicApiKey: z.string(),
    platform: z.union([
        z.enum([
            "macOS",
            "iOS"
        ]),
        z.string()
    ]),
    appUserId: z.string(),
    aliases: z.array(z.string()),
    vendorId: z.string(),
    appVersion: z.string(),
    osVersion: z.string(),
    deviceModel: z.string(),
    deviceLocale: z.string(),
    deviceLanguageCode: z.string(),
    deviceCurrencyCode: z.string(),
    deviceCurrencySymbol: z.string(),
    // What is this value really?
    timezoneOffset: z.number(),
    radioType: z.string(),
    interfaceType: z.string(),
    interfaceStyle: z.string(),
    isLowPowerModeEnabled: z.boolean(),
    bundleId: z.string(),
    // ISO?
    appInstallDate: z.string(),
    isMac: z.boolean(),
    daysSinceInstall: z.number(),
    minutesSinceInstall: z.number(),
    daysSinceLastPaywallView: z.number().optional(),
    minutesSinceLastPaywallView: z.number().optional(),
    totalPaywallViews: z.number(),
    utcDate: z.string(),
    localDate: z.string(),
    utcTime: z.string(),
    localTime: z.string(),
    utcDateTime: z.string(),
    localDateTime: z.string(),
    isSandbox: z.string(),
    subscriptionStatus: z.string(),
    isFirstAppOpen: z.boolean(),
    sdkVersion: z.string(),
    sdkVersionPadded: z.string(),
    appBuildString: z.string(),
    appBuildStringNumber: z.number().optional()
});
export const DevicePropertiesDefinition = {
    publicApiKey: {
        description: "The Public API key for Superwall",
        exampleValues: [
            "pk_79e750d225197c7d774602f5698b7510dad0dfe15d91464a"
        ],
        availableSinceVersion: "3.0.0"
    },
    platform: {
        description: "The platform the user is on",
        exampleValues: [
            "macOS",
            "iOS"
        ],
        availableSinceVersion: "3.0.0"
    },
    appUserId: {
        description: "The unique identifier for the user, provided by the app. This can also be a `$SuperwallAlias` if you have not called `identify`",
        exampleValues: [
            "2c79db670ec39dd8e747ae2ff4",
            "$SuperwallAlias:9b8663b5-eb67-41fd-a5a1-972ea2149e23"
        ],
        availableSinceVersion: "3.0.0"
    },
    aliases: {
        description: "A list of aliases for the user. If you've called `identify` this may contain the alias Superwall originally created.",
        exampleValues: [
            "9b8663b5-eb67-41fd-a5a1-972ea2149e23"
        ],
        availableSinceVersion: "3.0.0"
    },
    vendorId: {
        description: "A unique identifier for the device. This persists between `identify` and `reset` calls",
        exampleValues: [
            "9b8663b5-eb67-41fd-a5a1-972ea2149e23"
        ],
        availableSinceVersion: "3.0.0"
    },
    appVersion: {
        description: "The version of the app, as defined in the app's `Info.plist`",
        exampleValues: [
            "1.0.0",
            "4.3.1"
        ],
        availableSinceVersion: "3.0.0"
    },
    osVersion: {
        description: "The version of the operating system",
        exampleValues: [
            "16.3.1",
            "14.4.1"
        ],
        availableSinceVersion: "3.0.0"
    },
    deviceModel: {
        description: "The model of the device",
        exampleValues: [
            "iPhone12",
            "iPhone8",
            "iPad5"
        ],
        availableSinceVersion: "3.0.0"
    },
    deviceLocale: {
        description: "The locale of the device",
        exampleValues: [
            "en_US",
            "en_GB",
            "fr_FR",
            "fr_OM"
        ],
        availableSinceVersion: "3.0.0"
    },
    deviceLanguageCode: {
        description: "The language code of the device, just the first part of the locale",
        exampleValues: [
            "en",
            "fr"
        ],
        availableSinceVersion: "3.0.0"
    },
    deviceCurrencyCode: {
        description: "The currency code of the device",
        exampleValues: [
            "USD",
            "EUR"
        ],
        availableSinceVersion: "3.0.0"
    },
    deviceCurrencySymbol: {
        description: "The currency symbol of the device",
        exampleValues: [
            "$",
            "€"
        ],
        availableSinceVersion: "3.0.0"
    },
    timezoneOffset: {
        description: "The timezone offset of the device in seconds. Ex: -14,400 for EST",
        exampleValues: [
            -18000,
            3600
        ],
        availableSinceVersion: "3.0.0"
    },
    radioType: {
        description: "The radio type of the device",
        exampleValues: [
            "Cellular",
            "Wifi",
            "No Internet"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    interfaceType: {
        description: "The type of interface of the device, preferred over `deviceModel`. Note that iPhone screen size emulated in iPad will be iphone. Built for iPad on Mac will be ipad.",
        exampleValues: [
            "ipad",
            "iphone",
            "mac",
            "carplay",
            "tv",
            "unspecified"
        ],
        availableSinceVersion: "3.2.0"
    },
    interfaceStyle: {
        description: "The interface style of the device",
        exampleValues: [
            "Unspecified",
            "Unknown",
            "Light",
            "Dark"
        ],
        availableSinceVersion: "3.0.0"
    },
    isLowPowerModeEnabled: {
        description: "Whether or not low power mode is enabled",
        exampleValues: [
            "true",
            "false"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    bundleId: {
        description: "The bundle ID of the app",
        exampleValues: [
            "com.superwall.test"
        ],
        availableSinceVersion: "3.0.0"
    },
    appInstallDate: {
        description: "The date the app was installed, ISO-8601 format",
        exampleValues: [
            "2021-07-01T00:00:00.000Z"
        ],
        availableSinceVersion: "3.0.0"
    },
    isMac: {
        description: "Whether or not the device is a Mac",
        exampleValues: [
            true,
            false
        ],
        availableSinceVersion: "3.0.0"
    },
    daysSinceInstall: {
        description: "The number of days since the app was installed",
        exampleValues: [
            0,
            1,
            2
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    minutesSinceInstall: {
        description: "The number of minutes since the app was installed",
        exampleValues: [
            0,
            1,
            2
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    daysSinceLastPaywallView: {
        description: "The number of days since the last paywall view",
        exampleValues: [
            undefined,
            0,
            1,
            2
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    minutesSinceLastPaywallView: {
        description: "The number of minutes since the last paywall view",
        exampleValues: [
            undefined,
            0,
            1,
            2
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    totalPaywallViews: {
        description: "The total number of paywall views",
        exampleValues: [
            0,
            1,
            2
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    utcDate: {
        description: "The UTC date",
        exampleValues: [
            "2021-07-01"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    localDate: {
        description: "The local date",
        exampleValues: [
            "2021-07-01"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    utcTime: {
        description: "The UTC time",
        exampleValues: [
            "15:54:28"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    localTime: {
        description: "The local time",
        exampleValues: [
            "15:54:28"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    localDateTime: {
        description: "The local date time",
        exampleValues: [
            "2021-07-01T15:54:28"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    utcDateTime: {
        description: "The UTC date time",
        exampleValues: [
            "2021-07-01T15:54:28"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    isSandbox: {
        description: "Whether or not the device is a sandbox",
        exampleValues: [
            "true",
            "false"
        ],
        availableSinceVersion: "3.0.0"
    },
    subscriptionStatus: {
        description: "The subscription status of the user",
        exampleValues: [
            "ACTIVE",
            "INACTIVE",
            "UNKNOWN"
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    isFirstAppOpen: {
        description: "Whether or not this is the first app open",
        exampleValues: [
            true,
            false
        ],
        availableSinceVersion: "3.0.0",
        updatesFrequently: true
    },
    sdkVersion: {
        description: "The version of the Superwall SDK",
        exampleValues: [
            "3.0.0"
        ],
        availableSinceVersion: "3.4.0"
    },
    sdkVersionPadded: {
        description: "The version of the Superwall SDK, padded to 3 digits",
        exampleValues: [
            "003.000.010"
        ],
        availableSinceVersion: "3.4.0"
    },
    appBuildString: {
        description: "The build string of the app, usually a number. From the app's `Info.plist`",
        exampleValues: [
            "1234"
        ],
        availableSinceVersion: "3.4.0"
    },
    appBuildStringNumber: {
        description: "The build string of the app, converted to a number",
        exampleValues: [
            undefined,
            1
        ],
        availableSinceVersion: "3.4.0"
    }
};
// TODO: Add device.daysSince_X @ 3.1.0
export const FrequentlyUpdatingDeviceProperties = Object.entries(DevicePropertiesDefinition).filter(([key, value])=>value.updatesFrequently).map(([key, value])=>key);
